/**
 * Thin fetch wrapper. Per CONVENTIONS §D + TZ-2B-04: every outbound call
 * carries a W3C `traceparent` header so api-side logs correlate with the
 * UI-side trace. We mint a fresh traceparent per session (sufficient for
 * Phase 2; later we'll plumb OTel browser SDK).
 *
 * Errors come back as RFC 7807 Problem Details
 * (`Content-Type: application/problem+json`); we surface them as
 * typed `ApiError` instances so callers don't parse JSON twice.
 */
import { mintTraceparent } from "./trace";

const SESSION_TRACEPARENT = mintTraceparent();

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  code: string;
  instance?: string;
  request_id?: string;
  extra?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail || problem.title);
    this.name = "ApiError";
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
  }
}

export interface ApiClientOptions {
  baseUrl?: string;
  /** Bearer token (Sanctum API key) — read from auth store at call time. */
  getToken?: () => string | null;
  /**
   * Fired when a response proves the stored session is gone server-side
   * (401 with UNAUTHENTICATED / TOKEN_INVALID / TOKEN_EXPIRED) so the app
   * can clear the persisted auth state and route to login. NOT fired for
   * /v1/auth/* endpoints — a wrong password is also a 401, and the auth
   * pages own their own error display. The ApiError is still thrown
   * afterwards, so per-call catch blocks keep working.
   */
  onAuthError?: (problem: ProblemDetails, path: string) => void;
}

/** Codes minted by AuthSanctumOrJwt when the bearer token itself is bad. */
const SESSION_GONE_CODES = new Set([
  "UNAUTHENTICATED",
  "TOKEN_INVALID",
  "TOKEN_EXPIRED",
]);

function isAuthEndpoint(path: string): boolean {
  return path.startsWith("/v1/auth/");
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
  /**
   * Pre-built body (e.g. FormData for multipart uploads) sent verbatim.
   * Bypasses JSON serialization; Content-Type is left unset so the browser
   * adds the multipart boundary. Mutually exclusive with `json`.
   */
  rawBody?: BodyInit;
}

/**
 * Build a request-scoped fetcher. The `auth` store wires this up at boot
 * with `getToken: () => state.token`.
 */
export function createApiClient(opts: ApiClientOptions = {}) {
  const baseUrl = opts.baseUrl ?? "";

  async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    headers.set("traceparent", SESSION_TRACEPARENT);

    const token = opts.getToken?.();
    if (token !== null && token !== undefined && token !== "") {
      headers.set("Authorization", `Bearer ${token}`);
    }

    let body: BodyInit | undefined;
    if (init.rawBody !== undefined) {
      // FormData sets its own multipart Content-Type (with boundary).
      body = init.rawBody;
    } else if (init.json !== undefined) {
      // Don't clobber merge-patch+json (set by patch()).
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
      body = JSON.stringify(init.json);
    }

    const response = await fetch(baseUrl + path, { ...init, headers, body });
    const contentType = response.headers.get("content-type") ?? "";

    if (!response.ok) {
      if (contentType.includes("application/problem+json")) {
        const problem = (await response.json()) as ProblemDetails;
        if (
          problem.status === 401 &&
          SESSION_GONE_CODES.has(problem.code) &&
          !isAuthEndpoint(path)
        ) {
          opts.onAuthError?.(problem, path);
        }
        throw new ApiError(problem);
      }
      throw new ApiError({
        type: "about:blank",
        title: response.statusText || "Request failed",
        status: response.status,
        detail: `HTTP ${response.status}`,
        code: "HTTP_ERROR",
      });
    }

    if (response.status === 204 || !contentType.includes("application/json")) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  return {
    request,
    get: <T>(path: string, init?: RequestOptions) =>
      request<T>(path, { ...init, method: "GET" }),
    post: <T>(path: string, json?: unknown, init?: RequestOptions) =>
      request<T>(path, { ...init, method: "POST", json }),
    patch: <T>(path: string, json?: unknown, init?: RequestOptions) =>
      request<T>(path, {
        ...init,
        method: "PATCH",
        json,
        headers: {
          ...(init?.headers as Record<string, string> | undefined),
          "Content-Type": "application/merge-patch+json",
        },
      }),
    delete: <T>(path: string, init?: RequestOptions) =>
      request<T>(path, { ...init, method: "DELETE" }),
  };
}
